import React, { useState } from 'react'
import { Dialog } from '@headlessui/react'
import Button from '@atlaskit/button'
import WarningIcon from '@atlaskit/icon/glyph/warning'
import TrashIcon from '@atlaskit/icon/glyph/trash'
import RefreshIcon from '@atlaskit/icon/glyph/refresh'
import { token } from '@atlaskit/tokens'

/**
 * CONFIRM RUN ACTION MODAL
 *
 * Confirmation modal for destructive run actions (delete, clear).
 * Shows appropriate warning based on action type with Jira-style UI.
 *
 * @param {boolean} isOpen - Whether modal is open
 * @param {function} onClose - Callback to close modal
 * @param {function} onConfirm - Async callback when user confirms action
 * @param {string} actionType - 'delete' or 'clear' - determines styling and messaging
 * @param {string} runName - Name of the run for display (optional)
 * @param {number} runNumber - Run number for display (1-indexed)
 */
export function ConfirmRunActionModal({
  isOpen,
  onClose,
  onConfirm,
  actionType = 'delete',
  runName = '',
  runNumber = 1
}) {
  // Loading state for async confirm operation
  const [isLoading, setIsLoading] = useState(false)

  // Format run number with leading zero (e.g., "01", "02")
  const formattedRunNumber = String(runNumber).padStart(2, '0')

  // Configuration based on action type - controls icons, colors, and messaging
  const config = {
    delete: {
      title: 'Delete Run',
      icon: <TrashIcon label="Delete" size="medium" primaryColor={token('color.icon.danger', '#DE350B')} />,
      iconBg: token('color.background.danger', '#FFEBE6'),
      message: `Are you sure you want to delete Run #${formattedRunNumber}${runName ? ` (${runName})` : ''}?`,
      warning: 'This action cannot be undone. All execution data, audit logs, and linked bugs for this run will be permanently deleted.',
      confirmText: 'Delete Run',
      confirmAppearance: 'danger'
    },
    clear: {
      title: 'Clear Run',
      icon: <RefreshIcon label="Clear" size="medium" primaryColor={token('color.icon.warning', '#FF8B00')} />,
      iconBg: token('color.background.warning', '#FFFAE6'),
      message: `Are you sure you want to clear all results from Run #${formattedRunNumber}${runName ? ` (${runName})` : ''}?`,
      warning: 'This will reset all test case and test step execution statuses to empty. The run structure will be preserved.',
      confirmText: 'Clear Run',
      confirmAppearance: 'warning'
    }
  }

  // Get config for current action type, default to delete if invalid
  const { title, icon, iconBg, message, warning, confirmText, confirmAppearance } = config[actionType] || config.delete

  /**
   * Handle confirm action with loading state
   * Calls onConfirm callback and closes modal on success
   */
  const handleConfirm = async () => {
    setIsLoading(true)
    try {
      await onConfirm()
      onClose()
    } catch (error) {
      // Error notification handled by caller - keep modal open for retry
      console.error(`${actionType} run failed:`, error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={isOpen} onClose={onClose} className="headless-dialog-root">
      {/* Dark semi-transparent backdrop overlay - matches Jira style */}
      <div
        className="headless-dialog-backdrop"
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'var(--ds-blanket)',
          zIndex: 1000
        }}
        aria-hidden="true"
      />

      {/* Modal Container - centered with flexbox, pointer-events:none to prevent backdrop clicks */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          pointerEvents: 'none'
        }}
      >
        <Dialog.Panel
          style={{
            backgroundColor: 'var(--ds-surface-overlay, #FFFFFF)',
            borderRadius: '8px',
            boxShadow: '0 8px 16px -4px rgba(9, 30, 66, 0.25), 0 0 1px rgba(9, 30, 66, 0.31)',
            width: '420px',
            maxHeight: '90vh',
            overflow: 'auto',
            pointerEvents: 'auto'
          }}
        >
          {/* Header - Icon and title in Jira confirmation style */}
          <div
            style={{
              padding: '20px 24px 16px',
              borderBottom: `1px solid ${token('color.border', '#DFE1E6')}`,
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}
          >
            {/* Circular icon background */}
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: iconBg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              {icon}
            </div>
            <Dialog.Title
              style={{
                fontSize: '20px',
                fontWeight: 500,
                color: token('color.text', '#172B4D'),
                margin: 0
              }}
            >
              {title}
            </Dialog.Title>
          </div>

          {/* Body - Confirmation message and warning */}
          <div style={{ padding: '20px 24px' }}>
            <p style={{
              fontSize: '14px',
              color: token('color.text', '#172B4D'),
              margin: '0 0 12px 0'
            }}>
              {message}
            </p>
            {/* Warning box with colored background */}
            <p style={{
              fontSize: '13px',
              color: token('color.text.subtlest', '#6B778C'),
              margin: 0,
              padding: '12px',
              backgroundColor: token('color.background.warning', '#FFFAE6'),
              borderRadius: '4px',
              border: `1px solid ${token('color.border.warning', '#FFE380')}`
            }}>
              {warning}
            </p>
          </div>

          {/* Footer - Cancel and confirm buttons */}
          <div
            style={{
              padding: '16px 24px',
              borderTop: `1px solid ${token('color.border', '#DFE1E6')}`,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px'
            }}
          >
            <Button
              appearance="subtle"
              onClick={onClose}
              isDisabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              appearance={confirmAppearance}
              onClick={handleConfirm}
              isLoading={isLoading}
            >
              {confirmText}
            </Button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  )
}
